var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormController } from '../../controllers/form';
import { SlotController } from '../../controllers/slot-controller';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import styles from './button.styles';
/**
 * @slot before - The component before content
 * @slot default - The component content
 * @slot after - The component after content
 * @slot custom-link - The component's custom link slot
 * @csspart button - The component's button element
 * @csspart base - The component's base wrapper.
 * @csspart before-wrapper - The component's before slot wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart text - The component's text content.
 * @csspart part-default - The component's default slot.
 * @csspart after-wrapper - The component's after slot wrapper.
 * @csspart part-after - The component's after slot.
 * @csspart part-custom-link - The component's custom link slot
 */
export class BlocksButton extends BlocksElement {
    constructor() {
        super(...arguments);
        this.formController = new FormController(this);
        /**
         * Size variant
         * <cat-text-passage size="sm">
         * <ul>
         * <li>**xs** decreases overall size for icon only buttons of 24px height</li>
         * <li>**sm** decreases overall size for buttons of 32px height</li>
         * <li>**md** increases overall size for buttons of 40px height</li>
         * <li>**lg** increases overall size for buttons of 56px height</li>
         * </ul>
         * </cat-text-passage>
         */
        this.size = 'md';
        /**
         * Visually hide button text (but text is still accessible to assistive technology)
         * 1) Use this for icon-only buttons for accessibility
         */
        this.hideText = false;
        /**
         * Type of button
         */
        this.type = 'button';
        /**
         * Used to prevent HTML semantic button from being sequentially focusable. If tabIndex is set on host element,
         * host also gets focus on android screen reader mode. To prevent it, this property can be used.
         * @ignore
         */
        this.skipTab = false;
        this.slots = new SlotController(this, 'before', null, 'custom-link', 'after');
        this.internals_ = this.attachInternals?.();
        /**
         * Prevent the click event from being emitted when the button is disabled
         */
        this.handleHostClick = (event) => {
            if (this.disabled) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        };
    }
    /**
     * Add a static formAssociated property to custom element class.
     * This tells the browser to treat the element like a form control
     */
    static { this.formAssociated = true; }
    static { this.styles = styles; }
    //Since TS expects both setter and getter type to be same adding this ts-ignore
    set form(val) {
        this._form = val;
    }
    /**
     * Associates the control with a form element
     */
    //Since TS expects both setter and getter type to be same adding any type surpass ts error
    get form() {
        return (this.internals_?.form ?? this.formController.form);
    }
    get hasCustomLinkSlot() {
        return this.slots.hasSlotted('custom-link');
    }
    /**
     * Connected callback lifecycle
     */
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this.handleHostClick);
    }
    /**
     * disConnected callback lifecycle
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this.handleHostClick);
    }
    updated(_changedProperties) {
        if (_changedProperties.has('autoFocus') && this.autoFocus) {
            this.focus();
        }
    }
    focus() {
        this.buttonEl?.focus();
    }
    /**
     * when we click on button which has type=submit trigger requestSubmit on closest form element in
     * order to invoke submit event on form element
     *
     * 1) If the submitter button has a name attribute, its data will be included in the form submission.
     */
    handleClick() {
        if (this.type === 'reset') {
            this.formController.submit(this.type);
        }
        else if (this.type === 'submit') {
            /**1 */
            if (this.name && this.value) {
                this.internals_?.setFormValue(this.value);
            }
            const submitter = this.shadowRoot?.querySelector('button')?.cloneNode(true);
            this.formController.submit(this.type, submitter);
        }
    }
    /**
     * Handle on keydown/click
     * 1) If key selected is enter/clicked, navigate to the custom link
     */
    handleCustomLinkKeyEvent(e) {
        if (this.hasCustomLinkSlot) {
            if ((e.type === 'click' || (e instanceof KeyboardEvent && e.key === KEYS.ENTER)) && this._children) {
                this._children[0].click();
            }
        }
    }
    render() {
        const componentClassName = this.componentClassNames('cat-c-button', {
            'cat-c-button--primary': this.variant === 'primary',
            'cat-c-button--contrast': this.variant === 'contrast',
            'cat-c-button--ghost': this.variant === 'ghost',
            'cat-c-button--danger': this.variant === 'danger',
            'cat-c-button--ghost-danger': this.variant === 'ghost-danger',
            'cat-c-button--inline': this.variant === 'inline',
            'cat-c-button--xs': this.size === 'xs',
            'cat-c-button--sm': this.size === 'sm',
            'cat-c-button--md': this.size === 'md',
            'cat-c-button--lg': this.size === 'lg',
            'cat-c-button--full-width': this.fullWidth === true,
            'cat-c-button--icon-only': this.hideText === true,
            'cat-c-button--inverted': this.inverted === true,
            'cat-c-button--icon-top': this.iconPosition === 'top',
            'cat-c-button--has-before': this.slots.hasSlotted('before'),
            'cat-c-button--has-after': this.slots.hasSlotted('after')
        });
        if (this.href) {
            return html `
        <a
          part="base"
          href="${ifDefined(this.href)}"
          role="button"
          class="${componentClassName}"
          aria-disabled=${this.disabled ? 'true' : 'false'}
          ?disabled=${this.disabled}
          tabindex=${this.disabled ? -1 : 0}
          download=${ifDefined(this.download ? this.download : undefined)}
          aria-label=${ifDefined(this.label)}
          aria-pressed=${ifDefined(this.pressed)}
          aria-haspopup=${ifDefined(this.hasPopup)}
          aria-expanded=${ifDefined(this.expanded)}
          aria-controls=${ifDefined(this.ariaControls)}
          target=${ifDefined(this.target)}
        >
          <span part="before-wrapper" class="cat-c-button__before"><slot part="part-before" name="before"></slot></span>
          <span part="text" class="${this.hideText ? 'cat-u-is-vishidden cat-c-button__text' : 'cat-c-button__text'}"
            ><slot part="part-default"></slot
          ></span>
          <span part="after-wrapper" class="cat-c-button__after"><slot part="part-after" name="after"></slot></span>
        </a>
      `;
        }
        else if (this.hasCustomLinkSlot) {
            return html `
        <div
          part="base"
          role="button"
          class="${componentClassName}"
          aria-disabled=${this.disabled ? 'true' : 'false'}
          ?data-disabled=${this.disabled}
          tabindex=${this.disabled ? -1 : 0}
          aria-label=${ifDefined(this.label)}
          aria-pressed=${ifDefined(this.pressed)}
          aria-expanded=${ifDefined(this.expanded)}
          aria-haspopup=${ifDefined(this.hasPopup)}
          aria-controls=${ifDefined(this.ariaControls)}
          @click=${this.handleCustomLinkKeyEvent}
          @keydown=${this.handleCustomLinkKeyEvent}
        >
          <slot part="part-custom-link" name="custom-link" tabindex=${-1}></slot>
        </div>
      `;
        }
        else {
            return html `
        <button
          part="base button"
          @click=${this.handleClick}
          class="${componentClassName}"
          .type=${this.type}
          tabindex=${ifDefined(this.skipTab ? '-1' : undefined)}
          value=${ifDefined(this.value)}
          name=${ifDefined(this.name)}
          formenctype=${ifDefined(this.formEnctype)}
          formmethod=${ifDefined(this.formMethod)}
          ?formnovalidate=${this.formNoValidate}
          formtarget=${ifDefined(this.formTarget)}
          formaction=${ifDefined(this.formAction)}
          aria-current=${ifDefined(this.currentStateIndicator)}
          aria-label=${ifDefined(this.label)}
          ?autofocus=${this.autoFocus}
          ?disabled=${this.disabled}
          aria-expanded=${ifDefined(this.expanded)}
          aria-haspopup=${ifDefined(this.hasPopup)}
        >
          <span part="before-wrapper" class="cat-c-button__before"><slot part="part-before" name="before"></slot></span>
          <span part="text" class="${this.hideText ? 'cat-u-is-vishidden cat-c-button__text' : 'cat-c-button__text'}"
            ><slot part="part-default"></slot
          ></span>
          <span part="after-wrapper" class="cat-c-button__after"><slot part="part-after" name="after"></slot></span>
        </button>
      `;
        }
    }
}
__decorate([
    property({ type: String })
], BlocksButton.prototype, "ariaControls", void 0);
__decorate([
    property({ type: String, reflect: true })
], BlocksButton.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], BlocksButton.prototype, "hasPopup", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "target", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "href", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "name", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "label", void 0);
__decorate([
    property({ type: Boolean })
], BlocksButton.prototype, "currentStateIndicator", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksButton.prototype, "autoFocus", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "pressed", void 0);
__decorate([
    property({ type: Boolean })
], BlocksButton.prototype, "expanded", void 0);
__decorate([
    property({ type: String, reflect: true })
], BlocksButton.prototype, "size", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksButton.prototype, "hideText", void 0);
__decorate([
    property({ type: Boolean })
], BlocksButton.prototype, "inverted", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksButton.prototype, "fullWidth", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "type", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "iconPosition", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "download", void 0);
__decorate([
    property({ type: Boolean })
], BlocksButton.prototype, "skipTab", void 0);
__decorate([
    property({ attribute: 'formaction', type: String })
], BlocksButton.prototype, "formAction", void 0);
__decorate([
    property({ attribute: 'formenctype', type: String })
], BlocksButton.prototype, "formEnctype", void 0);
__decorate([
    property({ attribute: 'formmethod', type: String })
], BlocksButton.prototype, "formMethod", void 0);
__decorate([
    property({ attribute: 'formnovalidate', type: Boolean })
], BlocksButton.prototype, "formNoValidate", void 0);
__decorate([
    property({ attribute: 'formtarget', type: String })
], BlocksButton.prototype, "formTarget", void 0);
__decorate([
    property({ type: String })
], BlocksButton.prototype, "form", null);
__decorate([
    query('.cat-c-button')
], BlocksButton.prototype, "buttonEl", void 0);
if (customElements.get('cat-button') === undefined) {
    customElements.define('cat-button', BlocksButton);
}
